import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import {
  GKCheckbox,
  GKClientDataGrid,
  Grid2,
  StatusField,
  Typography,
} from "components";
import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { moneyCommaSeparator, moneyCommaSeparatorPoint } from "utils";
import { extraColumnsField } from "utils/helpers";

const GenerateTradeTable = ({
  watchAllocationType,
  watchAllocationBy,
}: {
  watchAllocationType: string;
  watchAllocationBy: string;
}) => {
  const form = useFormContext();

  const freezeData = useWatch({
    name: "allocation.frozenCompanies",
    control: form.control,
  });

  const holdData = useWatch({
    name: "allocation.holdCompanies",
    control: form.control,
  });

  const isFrozen = (freezeData: any[], id: string, gridCode: string) =>
    freezeData.some((item) => item.id === id && item.gridCode === gridCode);

  // const handleToggleAllFreeze = useCallback(
  //   (row: any, checked: boolean) => {
  //     const current = form.getValues("allocation.frozenCompanies");

  //     if (checked) {
  //       const updated = [
  //         ...current.filter((item: { id: number }) => item.id !== row.id),
  //         ...row.plan.map((item: any) => ({
  //           id: row.id,
  //           cash: row.cash,
  //           gridCode: item.gridCode,
  //         })),
  //       ];
  //       form.setValue("allocation.frozenCompanies", updated, {
  //         shouldDirty: true,
  //       });
  //     } else {
  //       const updated = current.filter(
  //         (item: { id: number }) => item.id !== row.id,
  //       );
  //       form.setValue("allocation.frozenCompanies", updated, {
  //         shouldDirty: true,
  //       });
  //     }
  //   },
  //   [form],
  // );

  const handleToggleSingleFreeze = useCallback(
    (row: any, gridCode: string, checked: boolean) => {
      const current = form.getValues("allocation.frozenCompanies");

      const isAlreadyFrozen = current.some(
        (item: { id: number; gridCode: string }) =>
          item.id === row.id && item.gridCode === gridCode,
      );

      let updated: any[];

      if (checked && !isAlreadyFrozen) {
        updated = [...current, { id: row.id, cash: row.cash, gridCode }];
      } else {
        updated = current.filter(
          (item: { id: number; gridCode: string }) =>
            !(item.id === row.id && item.gridCode === gridCode),
        );
      }

      form.setValue("allocation.frozenCompanies", updated, {
        shouldDirty: true,
      });
    },
    [form],
  );

  // const handleToggleAllHold = useCallback(
  //   (row: any, checked: boolean) => {
  //     const current = form.getValues("allocation.holdCompanies");

  //     if (checked) {
  //       const updated = [
  //         ...current.filter((item: { id: number }) => item.id !== row.id),
  //         ...row.plan.map((item: any) => ({
  //           id: row.id,
  //           cash: row.cash,
  //           gridCode: item.gridCode,
  //         })),
  //       ];
  //       form.setValue("allocation.holdCompanies", updated, {
  //         shouldDirty: true,
  //       });
  //     } else {
  //       const updated = current.filter(
  //         (item: { id: number }) => item.id !== row.id,
  //       );
  //       form.setValue("allocation.holdCompanies", updated, {
  //         shouldDirty: true,
  //       });
  //     }
  //   },
  //   [form],
  // );

  const handleToggleSingleHold = useCallback(
    (row: any, gridCode: string, checked: boolean) => {
      const current = form.getValues("allocation.holdCompanies");

      const isAlreadyFrozen = current.some(
        (item: { id: number; gridCode: string }) =>
          item.id === row.id && item.gridCode === gridCode,
      );

      let updated: any[];

      if (checked && !isAlreadyFrozen) {
        updated = [...current, { id: row.id, cash: row.cash, gridCode }];
      } else {
        updated = current.filter(
          (item: { id: number; gridCode: string }) =>
            !(item.id === row.id && item.gridCode === gridCode),
        );
      }

      form.setValue("allocation.holdCompanies", updated, {
        shouldDirty: true,
      });
    },
    [form],
  );

  const rebalanceColumns = () => {
    return [
      { ...extraColumnsField("Company Name", "company", 200, 1) },
      {
        ...extraColumnsField("Model WT.", "versionWeightage", 120, 1),
        type: "number",
        valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
      },
      {
        ...extraColumnsField("Portfolio WT.", "portfolioWeightage", 120, 1),
        type: "number",
        valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
      },
      {
        ...extraColumnsField("Deviation WT.", "deviationPercent", 120, 1),
        type: "number",
        valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
      },
      {
        ...extraColumnsField("Deviation (₹)", "deviation", 120, 1),
        type: "number",
        valueGetter: (value) => moneyCommaSeparator(value.value),
      },
      {
        ...extraColumnsField("Trans. type", "transaction type", 120, 1),
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          return <StatusField statusString={params.row["transaction type"]} />;
        },
      },
      {
        ...extraColumnsField("QTY", "quantity", 100, 1),
        type: "number",
        valueGetter: (params) => params.row.quantity || "0",
      },
      {
        ...extraColumnsField("CMP (₹)", "cmp", 120, 1),
        type: "number",
        valueGetter: (value) => moneyCommaSeparator(value.value),
      },
      {
        ...extraColumnsField("Trade Amount (₹)", "amount", 140, 1),
        type: "number",
        valueGetter: (value) => moneyCommaSeparator(value.value),
      },
      {
        ...extraColumnsField(
          "After Trade WT. (₹)",
          "afterTradeWeightage",
          150,
          1,
        ),
        type: "number",
        valueFormatter: (params) => `${Number(params.value || 0)?.toFixed(2)}%`,
      },
    ] as GridColDef[];
  };

  const CompaniesTable = (params: GridRowParams) => {
    const { row } = params;

    const handleChildColumns = (type: string, allocBy: string) => {
      const caseKey = `${type}-${allocBy}`;

      switch (caseKey) {
        case "allocation-version":
          return [
            { ...extraColumnsField("Company Name", "company", 200, 1) },
            {
              ...extraColumnsField("Model WT.", "versionWeightage", 100, 1),
              type: "number",
              valueGetter: (params) =>
                `${(params.row.versionWeightage || 0).toFixed(2)}%`,
            },
            {
              ...extraColumnsField(
                "Portfolio WT.",
                "portfolioWeightage",
                120,
                1,
              ),
              type: "number",
              valueGetter: (params) =>
                `${(params.row.portfolioWeightage || 0).toFixed(2)}%`,
            },
            {
              ...extraColumnsField(
                "Allocation WT.",
                "allocationWeightage",
                120,
                1,
              ),
              type: "number",
              valueGetter: (params) =>
                `${(params.row.allocationWeightage || 0).toFixed(2)}%`,
            },
            {
              ...extraColumnsField("Trans. Type", "transaction type", 100, 1),
              align: "center",
              headerAlign: "center",
              renderCell: (params) => {
                return (
                  <StatusField statusString={params.row["transaction type"]} />
                );
              },
            },
            {
              ...extraColumnsField("QTY", "quantity", 100, 1),
              type: "number",
              valueGetter: (params) => params.row.quantity || "0",
            },
            {
              ...extraColumnsField("CMP", "cmp", 100, 1),
              type: "number",
              valueGetter: (value) => moneyCommaSeparator(value.value),
            },
            {
              ...extraColumnsField("Trade Amount", "amount", 120, 1),
              type: "number",
              valueGetter: (value) => moneyCommaSeparator(value.value),
            },
            {
              ...extraColumnsField(
                "After Trade WT. (₹)",
                "afterTradeWeightage",
                150,
                1,
              ),
              type: "number",
              valueGetter: (params) =>
                `${(params.row.afterTradeWeightage || 0).toFixed(2)}%`,
            },
            {
              ...extraColumnsField("Freeze", "freeze", 80, 1),
              sortable: false,
              align: "center",
              headerAlign: "center",
              // renderHeader: () => {
              //   const allFrozen = row.plan.every((item: any) =>
              //     isFrozen(freezeData, row.id, item.gridCode),
              //   );

              //   return (
              //     <div
              //       style={{ display: "flex", alignItems: "center", gap: 4 }}
              //     >
              //       <GKCheckbox
              //         checked={allFrozen}
              //         onChange={(e) => {
              //           e.stopPropagation();
              //           handleToggleAllFreeze(row, e.target.checked);
              //         }}
              //       />
              //       <Typography>Freeze</Typography>
              //     </div>
              //   );
              // },
              renderCell: (params) => {
                const isChecked = isFrozen(
                  freezeData,
                  row.id,
                  params.row.gridCode,
                );

                return (
                  <GKCheckbox
                    sx={{ padding: "0" }}
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSingleFreeze(
                        row,
                        params.row.gridCode,
                        e.target.checked,
                      );
                    }}
                  />
                );
              },
            },
            {
              ...extraColumnsField("HOLD", "hold", 80, 1),
              sortable: false,
              align: "center",
              headerAlign: "center",
              // renderHeader: () => {
              //   const allHeld = row.plan.every((item: any) =>
              //     holdData?.some(
              //       (dataItem: any) =>
              //         dataItem?.id === row.id &&
              //         dataItem?.gridCode === item.gridCode,
              //     ),
              //   );

              //   return (
              //     <div
              //       style={{ display: "flex", alignItems: "center", gap: 4 }}
              //     >
              //       <GKCheckbox
              //         checked={allHeld}
              //         onChange={(e) => {
              //           e.stopPropagation();
              //           handleToggleAllHold(row, e.target.checked);
              //         }}
              //       />
              //       <Typography>Hold</Typography>
              //     </div>
              //   );
              // },
              renderCell: (params) => {
                const isHeld = holdData?.some(
                  (item: any) =>
                    item?.id === row?.id &&
                    item?.gridCode === params?.row?.gridCode,
                );

                return (
                  <GKCheckbox
                    sx={{ padding: 0 }}
                    checked={isHeld}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSingleHold(
                        row,
                        params?.row?.gridCode,
                        e.target.checked,
                      );
                    }}
                  />
                );
              },
            },
          ] as GridColDef[];

        case "exit-version":
        case "exit-portfolio":
          return [
            { ...extraColumnsField("Company Name", "company", 250, 1) },
            {
              ...extraColumnsField(
                "Portfolio WT.",
                "portfolioWeightage",
                120,
                1,
              ),
              type: "number",
              valueFormatter: (params) =>
                `${Number(params.value || 0)?.toFixed(2)}%`,
            },
            {
              ...extraColumnsField("trans. type", "transaction type", 120, 1),
              align: "center",
              headerAlign: "center",
              renderCell: (params) => {
                return (
                  <StatusField statusString={params.row["transaction type"]} />
                );
              },
            },
            {
              ...extraColumnsField("QTY", "quantity", 120, 1),
              type: "number",
              valueGetter: (params) => params.row.quantity || "0",
            },
            {
              ...extraColumnsField("cmp (₹)", "cmp", 150, 1),
              type: "number",
              valueGetter: (value) => moneyCommaSeparator(value.value),
            },
            {
              ...extraColumnsField("Exit Amount", "amount", 150, 1),
              type: "number",
              valueFormatter: (params) => moneyCommaSeparator(params.value),
              valueGetter: (params) => params.row.amount || "0",
            },
            {
              ...extraColumnsField(
                "After Trade WT. (₹)",
                "afterTradeWeightage",
                150,
                1,
              ),
              type: "number",
              valueFormatter: (params) =>
                `${Number(params.value || 0)?.toFixed(2)}%`,
            },
            {
              ...extraColumnsField("HOLD", "hold", 80, 1),
              sortable: false,
              align: "center",
              headerAlign: "center",
              renderCell: (params) => {
                const isHeld = holdData?.some(
                  (item: any) =>
                    item?.id === row?.id &&
                    item?.gridCode === params?.row?.gridCode,
                );

                return (
                  <GKCheckbox
                    sx={{ padding: 0 }}
                    checked={isHeld}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSingleHold(
                        row,
                        params?.row?.gridCode,
                        e.target.checked,
                      );
                    }}
                  />
                );
              },
            },
          ] as GridColDef[];

        case "rebalance-version":
          return [
            ...rebalanceColumns(),
            {
              ...extraColumnsField("Freeze", "freeze", 70, 1),
              sortable: false,
              align: "center",
              headerAlign: "center",
              // renderHeader: () => {
              //   const allFrozen = row.plan.every((item: any) =>
              //     isFrozen(freezeData, row.id, item.gridCode),
              //   );

              //   return (
              //     <div
              //       style={{ display: "flex", alignItems: "center", gap: 4 }}
              //     >
              //       <GKCheckbox
              //         checked={allFrozen}
              //         onChange={(e) => {
              //           e.stopPropagation();
              //           handleToggleAllFreeze(row, e.target.checked);
              //         }}
              //       />
              //       <Typography>Freeze</Typography>
              //     </div>
              //   );
              // },
              renderCell: (params) => {
                const isChecked = isFrozen(
                  freezeData,
                  row.id,
                  params.row.gridCode,
                );

                return (
                  <GKCheckbox
                    sx={{ padding: "0" }}
                    size="small"
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSingleFreeze(
                        row,
                        params.row.gridCode,
                        e.target.checked,
                      );
                    }}
                  />
                );
              },
            },
            {
              ...extraColumnsField("HOLD", "hold", 60, 1),
              sortable: false,
              align: "center",
              headerAlign: "center",
              // renderHeader: () => {
              //   const allHeld = row.plan.every((item: any) =>
              //     holdData?.some(
              //       (dataItem: any) =>
              //         dataItem?.id === row.id &&
              //         dataItem?.gridCode === item.gridCode,
              //     ),
              //   );

              //   return (
              //     <div
              //       style={{ display: "flex", alignItems: "center", gap: 4 }}
              //     >
              //       <GKCheckbox
              //         checked={allHeld}
              //         onChange={(e) => {
              //           e.stopPropagation();
              //           handleToggleAllHold(row, e.target.checked);
              //         }}
              //       />
              //       <Typography>Hold</Typography>
              //     </div>
              //   );
              // },
              renderCell: (params) => {
                const isHeld = holdData?.some(
                  (item: any) =>
                    item?.id === row?.id &&
                    item?.gridCode === params?.row?.gridCode,
                );

                return (
                  <GKCheckbox
                    sx={{ padding: 0 }}
                    checked={isHeld}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSingleHold(
                        row,
                        params?.row?.gridCode,
                        e.target.checked,
                      );
                    }}
                  />
                );
              },
            },
          ] as GridColDef[];
        case "rebalance-customized":
          return [
            {
              ...extraColumnsField(
                "Rebalance WT.",
                "rebalanceWeightage",
                120,
                1,
              ),
              type: "number",
              valueFormatter: (params) =>
                `${Number(params.value || 0)?.toFixed(2)}%`,
            },
            ...rebalanceColumns().filter(
              (dataItem) =>
                !["versionWeightage", "deviationPercent", "deviation"].includes(
                  dataItem.field,
                ),
            ),
          ] as GridColDef[];
        default:
          return [] as GridColDef[];
      }
    };

    return (
      <Grid2 container>
        <Grid2 size={12} p={1}>
          <GKClientDataGrid
            name="modelAllocationCreateOrder"
            pagination={false}
            showHeaderGrid={false}
            columns={handleChildColumns(watchAllocationType, watchAllocationBy)}
            pinnedColumns={{ left: ["company"] }}
            rows={
              row?.plan?.map((dataItem: any, i: any) => {
                return {
                  ...dataItem,
                  id: i,
                };
              }) || []
            }
            maxHeight={"calc(100vh - 524px)"}
          />
        </Grid2>
      </Grid2>
    );
  };

  const handleParentColumnsReturn = (type: string) => {
    switch (type) {
      case "exit":
        return [
          {
            ...extraColumnsField(
              "Portfolio Name / Code",
              "portfolioCode",
              250,
              1,
            ),
            renderCell: (params) => {
              return (
                <div>
                  <Typography fontWeight={700} whiteSpace={"nowrap"}>
                    {params?.row?.plan &&
                      params?.row?.plan[0][`portfolio name`]}
                  </Typography>
                  <Typography whiteSpace={"nowrap"}>
                    {params?.row?.plan &&
                      params?.row?.plan[0][`portfolio code`]}
                  </Typography>{" "}
                </div>
              );
            },
          },
          {
            ...extraColumnsField("Ledger Bal. (₹)", "ledgerBalance", 150, 1),
            type: "number",
            valueFormatter: (params) => moneyCommaSeparatorPoint(params.value),
          },
          {
            ...extraColumnsField(
              "current holding value (₹)",
              "currentAmount",
              200,
              1,
            ),
            type: "number",
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField("Exit of holdings %", "exitWeightage", 150, 1),
            type: "number",
            valueGetter: (params) =>
              `${Number(params?.row?.exitWeightage)?.toFixed(2)}%`,
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField("Exit Amount (₹)", "totalSellValue", 150, 1),
            type: "number",
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField(
              "After Trade Ledger Bal. (₹)",
              "afterTradeLedgerBalance",
              210,
              1,
            ),
            type: "number",
            valueGetter: (value) => moneyCommaSeparator(value.value),
          },
        ] as GridColDef[];
      case "rebalance":
        return [
          {
            ...extraColumnsField(
              "Portfolio name / code",
              "portfolioCode",
              210,
              1,
            ),
            renderCell: (params) => {
              return (
                <div>
                  <Typography fontWeight={700} whiteSpace={"nowrap"}>
                    {params?.row?.plan &&
                      params?.row?.plan[0][`portfolio name`]}
                  </Typography>
                  <Typography whiteSpace={"nowrap"}>
                    {params?.row?.plan &&
                      params?.row?.plan[0][`portfolio code`]}
                  </Typography>{" "}
                </div>
              );
            },
          },
          {
            ...extraColumnsField(
              "Current Holding Value (₹)",
              "currentAmount",
              180,
              1,
            ),
            type: "number",
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField("Ledger Bal. (₹)", "ledgerBalance", 150, 1),
            type: "number",
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField("Trade Amount (₹)", "tradeAmount", 150, 1),
            type: "number",
            valueGetter: (params) =>
              moneyCommaSeparator(
                Number(params?.row?.totalBuyValue) -
                  Number(params?.row?.totalSellValue),
              ),
          },
          {
            ...extraColumnsField(
              "Rebalance Amount (₹)",
              "rebalanceValue",
              150,
              1,
            ),
            type: "number",
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            field: "afterTradeLedgerBalance",
            headerName: "After Trade Ledger Balance",
            flex: 1,
            disableColumnMenu: true,
            valueGetter: (value) => moneyCommaSeparator(value.value),
          },
        ] as GridColDef[];
      case "allocation":
        return [
          {
            ...extraColumnsField(
              "Portfolio Name / Code",
              "portfolioCode",
              250,
              1,
            ),
            renderCell: (params) => {
              return (
                <div>
                  <Typography fontWeight={700} whiteSpace={"nowrap"}>
                    {params?.row?.plan &&
                      params?.row?.plan[0][`portfolio name`]}
                  </Typography>
                  <Typography whiteSpace={"nowrap"}>
                    {params?.row?.plan &&
                      params?.row?.plan[0][`portfolio code`]}
                  </Typography>{" "}
                </div>
              );
            },
          },
          {
            ...extraColumnsField(
              "Portfolio Value (₹)",
              "portfolioValue",
              150,
              1,
            ),
            type: "number",
            valueGetter: (params) => params.row.portfolioValue,
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField("Ledger Bal. (₹)", "ledgerBalance", 150, 1),
            type: "number",
            renderCell: (params) => (
              <Typography>
                {`${moneyCommaSeparator(params?.row?.ledgerBalance || 0)} [${
                  params?.row?.availableCashPercentage || 0
                }]`}
              </Typography>
            ),
          },
          {
            ...extraColumnsField(
              "Allocation of Ledger (%)",
              "allocationPercentage",
              150,
              1,
            ),
            type: "number",
            valueGetter: (params) =>
              `${Number(params?.row?.percentage)?.toFixed(2)}%`,
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField("Trade Amount (₹)", "amount", 150, 1),
            type: "number",
            valueGetter: (params) =>
              moneyCommaSeparator(params?.row?.totalBuyValue),
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
          {
            ...extraColumnsField(
              "After Trade Ledger Bal. (₹)",
              "afterTradeLedgerBalance",
              150,
              1,
            ),
            type: "number",
            valueGetter: (params) =>
              moneyCommaSeparator(params?.row?.afterTradeLedgerBalance),
            valueFormatter: (params) => moneyCommaSeparator(params.value),
          },
        ] as GridColDef[];
      default:
        return [] as GridColDef[];
    }
  };

  return (
    <GKClientDataGrid
      name="generateTradeTable"
      showHeaderGrid={false}
      columns={handleParentColumnsReturn(watchAllocationType)}
      maxHeight={"calc(100vh - 337px)"}
      getDetailPanelContent={CompaniesTable}
      rows={
        form.getValues("generateTradeResponse")?.map((data: any, i: number) => {
          return {
            ...data,
            index: i,
          };
        }) || []
      }
    />
  );
};

export default GenerateTradeTable;
